import { BenefitSection } from '~/generated/api-clients-generated'

export interface PlanSearchResult {
  key: string
  sectionId: BenefitSection
}

// Maps section keys to plain-language terms a user might search for,
// including synonyms that do not literally appear on the page.
const SEARCH_SYNONYMS: Readonly<Record<string, readonly string[]>> = {
  costs: [
    'cost',
    'costs',
    'price',
    'premium',
    'monthly premium',
    'monthly cost',
    'how much',
    'deductible',
    'out of pocket',
    'out-of-pocket',
    'maximum',
    'moop',
    'oop',
    'payment',
    'bill',
    'fee',
    'charge',
    'expense',
    'annual cost',
    'yearly cost',
    'total cost',
    'budget',
    'affordable',
    'what do i pay',
    'part b giveback',
    'giveback',
    'part b reduction',
    'rebate',
    'deductable',
    'out of pockit',
    'what will i owe'
  ],

  doctors: [
    'doctor',
    'doctors',
    'physician',
    'pcp',
    'primary care',
    'primary care doctor',
    'family doctor',
    'internist',
    'general practitioner',
    'gp',
    'specialist',
    'specialist visit',
    'referral',
    'provider',
    'my doctor',
    'my provider',
    'in network',
    'in-network',
    'out of network',
    'out-of-network',
    'covered doctor',
    'network',
    'office visit',
    'doctor visit',
    'see a doctor',
    'telehealth',
    'telemedicine',
    'video visit',
    'virtual visit',
    'phone visit',
    'online doctor',
    'remote visit',
    'visit',
    'tele',
    'tele health',
    'telemed',
    'virtual',
    'referal',
    'refferal'
  ],

  drugs: [
    'drug',
    'drugs',
    'prescription',
    'medication',
    'medications',
    'medicine',
    'medicines',
    'pills',
    'meds',
    'formulary',
    'drug list',
    'covered drugs',
    'drug coverage',
    'tier',
    'tier 1',
    'tier 2',
    'tier 3',
    'tier 4',
    'tier 5',
    'generic',
    'brand name',
    'brand-name',
    'preferred drug',
    'specialty drug',
    'part d',
    'drug deductible',
    'drug cost',
    'drug tier',
    'drug pricing',
    'rx',
    'precription',
    'prescriptions',
    'medcation',
    'medcine',
    'medecine',
    'formulery',
    'drug plan',
    'diabetes medication',
    'diabetes medicine',
    'diabetes drug',
    'diabetes pill',
    'diabetes insulin',
    'metformin',
    'ozempic',
    'insulin'
  ],

  pharmacy: [
    'pharmacy',
    'pharmacies',
    'drugstore',
    'drug store',
    'cvs',
    'walgreens',
    'rite aid',
    'publix',
    'walmart',
    'kroger',
    'costco',
    'fill prescription',
    'fill my prescription',
    'preferred pharmacy',
    'mail order',
    'mail-order',
    'mail order pharmacy',
    '90 day supply',
    '90-day supply',
    'pharmacy benefit',
    'prescription pickup',
    'farmacy',
    'phramacy',
    'pharmcy',
    'pharamcy'
  ],

  dental: [
    'dental',
    'dentist',
    'teeth',
    'tooth',
    'mouth',
    'oral',
    'oral health',
    'cleaning',
    'teeth cleaning',
    'checkup',
    'cavity',
    'filling',
    'crown',
    'bridge',
    'dentures',
    'false teeth',
    'root canal',
    'gum',
    'gum disease',
    'periodontal',
    'periodontist',
    'extraction',
    'tooth extraction',
    'pulled tooth',
    'implant',
    'dental implant',
    'toothache',
    'oral exam',
    'exam',
    'examination',
    'dental x-ray',
    'oral surgery',
    'endodontics',
    'prosthodontics',
    'denture',
    'partial denture',
    'full denture',
    'braces',
    'orthodontics',
    'periodental',
    'perio',
    'dential',
    'teath',
    'teeath',
    'copay',
    'co-pay',
    'coinsurance',
    'copayment',
    'co-payment',
    'co-insurance',
    'coinsurrance',
    'cleanings',
    'restorative',
    'periodontic',
    'maxillofacial',
    'surgery'
  ],

  hearing: [
    'hearing',
    'ear',
    'ears',
    'deaf',
    'deafness',
    'hard of hearing',
    'hearing loss',
    'hearing aid',
    'hearing aids',
    'hearing device',
    'audiologist',
    'audiology',
    'hearing test',
    'hearing exam',
    'ear exam',
    'exam',
    'examination',
    'tinnitus',
    'ringing in ears',
    'nationshearing',
    'hearing fitting',
    'hearing evaluation',
    'hearing allowance',
    'hard-of-hearing',
    'hearng',
    'hearring',
    'eardrum',
    'auditory',
    'copay',
    'co-pay',
    'coinsurance',
    'copayment',
    'co-payment',
    'co-insurance',
    'coinsurrance'
  ],

  vision: [
    'vision',
    'eye',
    'eyes',
    'glasses',
    'eyeglasses',
    'contact',
    'contacts',
    'contact lenses',
    'optometrist',
    'ophthalmologist',
    'eye doctor',
    'eye exam',
    'routine eye exam',
    'exam',
    'examination',
    'cataract',
    'cataract surgery',
    'glaucoma',
    'reading glasses',
    'frames',
    'eyeglass frames',
    'lenses',
    'eyeglass lenses',
    'sight',
    'eyesight',
    'blind',
    'low vision',
    'eyewear',
    'optician',
    'prescription glasses',
    'diabetic eye',
    'diabetic retinopathy',
    'macular degeneration',
    'vision allowance',
    'eye care',
    'upgrades',
    'lens upgrades',
    'cataracts',
    'opthamologist',
    'opthalmologist',
    'opthamology',
    'nearsighted',
    'near sighted',
    'farsighted',
    'far sighted',
    'glaucome',
    'retina',
    'retinal',
    'copay',
    'co-pay',
    'coinsurance',
    'copayment',
    'co-payment',
    'co-insurance',
    'coinsurrance'
  ],

  preventive: [
    'preventive',
    'preventative',
    'prevention',
    'annual visit',
    'wellness visit',
    'visit',
    'wellness exam',
    'exam',
    'examination',
    'annual physical',
    'physical exam',
    'physical',
    'checkup',
    'check-up',
    'annual checkup',
    'flu shot',
    'flu vaccine',
    'vaccine',
    'vaccination',
    'immunization',
    'shot',
    'mammogram',
    'breast cancer screening',
    'pap smear',
    'cervical cancer',
    'colonoscopy',
    'colon cancer screening',
    'bone density',
    'osteoporosis screening',
    'blood pressure check',
    'cholesterol test',
    'cancer screening',
    'prostate screening',
    'psa test',
    'glaucoma test',
    'welcome to medicare',
    'initial preventive visit',
    'annual wellness',
    'flu',
    'mammography',
    'mamogram',
    'mamagram',
    'physicial',
    'colonsocopy',
    'collonoscopy',
    'colon',
    'psa',
    'bp',
    'shingles',
    'shingrix',
    'pneumonia',
    'covid',
    'covid vaccine',
    'booster',
    'copay',
    'co-pay',
    'coinsurance',
    'copayment',
    'co-payment',
    'co-insurance',
    'coinsurrance',
    'dialysis',
    'renal',
    'kidney'
  ],

  emergency: [
    'emergency',
    'emergency room',
    'er',
    'hospital',
    'hospital stay',
    'admitted',
    'inpatient',
    'urgent care',
    'urgently needed',
    'ambulance',
    'air ambulance',
    'helicopter',
    'accident',
    'injury',
    'fall',
    'stroke',
    'heart attack',
    'crisis',
    'trauma',
    'surgery',
    'nursing home',
    'skilled nursing',
    'skilled nursing facility',
    'snf',
    'rehabilitation center',
    'rehab center',
    'long-term care',
    'long term care',
    'worldwide',
    'travel coverage',
    'travel emergency',
    'abroad',
    'international',
    'icu',
    'intensive care',
    'recovery',
    'acute care',
    'emergancy',
    'emergeny',
    'emergnecy',
    'ambluance',
    'amublance',
    'ambulence',
    'urgant',
    'urgant care',
    '911',
    'hospitalization',
    'er visit',
    'copay',
    'co-pay',
    'coinsurance',
    'copayment',
    'co-payment',
    'co-insurance',
    'coinsurrance'
  ],

  mental: [
    'mental health',
    'behavioral health',
    'depression',
    'anxiety',
    'stress',
    'therapy',
    'counseling',
    'counselor',
    'therapist',
    'psychiatrist',
    'psychologist',
    'social worker',
    'mental illness',
    'emotional health',
    'psychological',
    'mood disorder',
    'bipolar',
    'schizophrenia',
    'substance abuse',
    'alcohol',
    'addiction',
    'drug abuse',
    'opioid',
    'opioid treatment',
    'grief',
    'dementia',
    "alzheimer's",
    'memory',
    'memory loss',
    'group therapy',
    'individual therapy',
    'outpatient therapy',
    'psychiatric services',
    'inpatient mental health',
    'behavioral',
    'alzheimer',
    'alzheimers',
    'alzhiemers',
    'alzeimers',
    'altsheimers',
    'alzheimers disease',
    'demensia',
    'dimentia',
    'demenshia',
    'counceling',
    'counsiling',
    'counciling',
    'counsling',
    'psychatrist',
    'psycologist',
    'psyciatrist',
    'phsycologist',
    'copay',
    'co-pay',
    'coinsurance',
    'copayment',
    'co-payment',
    'co-insurance',
    'coinsurrance'
  ],

  diagnostic: [
    'blood test',
    'blood work',
    'lab',
    'lab work',
    'lab test',
    'laboratory',
    'x-ray',
    'xray',
    'x ray',
    'mri',
    'ct scan',
    'cat scan',
    'ultrasound',
    'scan',
    'imaging',
    'biopsy',
    'ekg',
    'ecg',
    'heart test',
    'radiology',
    'diagnostic',
    'test',
    'testing',
    'bone scan',
    'pet scan',
    'mammogram imaging',
    'nuclear imaging',
    'angiogram',
    'pathology',
    'urinalysis',
    'culture',
    'labwork',
    'xrays',
    'x rays',
    'ultra sound',
    'sonogram',
    'echo',
    'echocardiogram',
    'mri scan',
    'cat scan',
    'diagnostics',
    'a1c',
    'hemoglobin a1c',
    'diabetes test',
    'diabetes screening',
    'diabetes a1c',
    'blood sugar test',
    'glucose test',
    'cholesterol',
    'cbc',
    'cmp',
    'hdl',
    'ldl',
    'complete blood count',
    'comprehensive metabolic panel',
    'lipid panel',
    'tsh',
    'thyroid',
    'copay',
    'co-pay',
    'coinsurance',
    'copayment',
    'co-payment',
    'co-insurance',
    'coinsurrance',
    'radiological',
    'outpatient'
  ],

  equipment: [
    'wheelchair',
    'power wheelchair',
    'power chair',
    'motorized wheelchair',
    'walker',
    'rollator',
    'crutches',
    'cane',
    'scooter',
    'medical scooter',
    'mobility scooter',
    'electric scooter',
    'oxygen',
    'oxygen tank',
    'oxygen concentrator',
    'cpap',
    'bipap',
    'sleep apnea',
    'apnea',
    'hospital bed',
    'lift chair',
    'stair lift',
    'grab bar',
    'blood pressure monitor',
    'blood pressure cuff',
    'glucose monitor',
    'blood sugar monitor',
    'cgm',
    'test strips',
    'lancets',
    'insulin pump',
    'nebulizer',
    'breathing machine',
    'inhaler',
    'prosthetic',
    'prosthetics',
    'prosthesis',
    'artificial limb',
    'artificial leg',
    'artificial arm',
    'brace',
    'knee brace',
    'back brace',
    'orthotics',
    'orthotic inserts',
    'compression stockings',
    'medical equipment',
    'durable medical equipment',
    'dme',
    'diabetes',
    'diabetic',
    'diabetic supplies',
    'diabetes supplies',
    'diabetic shoes',
    'diabetic inserts',
    'therapeutic shoes',
    'shoes',
    'insulin',
    'part b insulin',
    'medical supplies',
    'wheel chair',
    'motorized scooter',
    'oxegen',
    'oxigen',
    'cpap machine',
    'c-pap',
    'sleep apnia',
    'nebuilzer',
    'nebuliser',
    'glucometer',
    'blood glucose',
    'bp monitor',
    'bp cuff',
    'cuff',
    'blood pressure cuff',
    'orthopedic',
    'orthopedics',
    'artifical limb',
    'artifical leg',
    'artifical arm',
    'copay',
    'co-pay',
    'coinsurance',
    'copayment',
    'co-payment',
    'co-insurance',
    'coinsurrance'
  ],

  therapy: [
    'physical therapy',
    'pt',
    'occupational therapy',

    'speech therapy',
    'speech language',
    'back pain',
    'back treatment',
    'spine',
    'neck pain',
    'joint pain',
    'rehabilitation',
    'rehab',
    'chiropractor',
    'chiropractic',
    'spinal adjustment',
    'adjustment',
    'outpatient surgery',
    'outpatient',
    'surgery center',
    'ambulatory surgery',
    'radiation',
    'radiation therapy',
    'chemo',
    'chemotherapy',
    'cancer treatment',
    'pulmonary rehab',
    'cardiac rehab',
    'heart rehab',
    'balance',
    'fall prevention',
    'swallowing',
    'swallowing therapy',
    'stroke recovery',
    'diabetes education',
    'diabetes self management',
    'hip',
    'knee',
    'shoulder',
    'podiatry',
    'podiatrist',
    'foot doctor',
    'foot care',
    'foot pain',
    'bunion',
    'nail care',
    'toenail',
    'theraphy',
    'therepy',
    'chiropracter',
    'chiropracter',
    'speach',
    'speach therapy',
    'speech',
    'occupational',
    'pulmonary',
    'cardiac',
    'physical therapist',
    'pt visit',
    'pain',
    'visit',
    'copay',
    'co-pay',
    'coinsurance',
    'copayment',
    'co-payment',
    'co-insurance',
    'coinsurrance'
  ],

  moreBenefits: [
    'home care',
    'home health',
    'home health care',
    'visiting nurse',
    'nurse',
    'home visit',
    'in-home care',
    'in home care',
    'home aide',
    'caregiver',
    'home nursing',
    'skilled care at home',
    'transportation',
    'ride',
    'car',
    'taxi',
    'uber',
    'lyft',
    'medical transport',
    'nemt',
    'non emergency transport',
    'ride to doctor',
    'over the counter',
    'otc',
    'otc benefit',
    'vitamins',
    'supplements',
    'bandages',
    'pain relief',
    'aspirin',
    'tylenol',
    'cold medicine',
    'cvs wallet',
    'extra benefits card',
    'allowance',
    'gym',
    'exercise',
    'silversneakers',
    'silver sneakers',
    'fitness',
    'fitness center',
    'fitness membership',
    'ymca',
    'workout',
    'swimming',
    'walking club',
    'meals',
    'food',
    'meal delivery',
    'meals on wheels',
    'nutrition',
    'meal benefit',
    'extra help',
    'extra perks',
    'additional benefits',
    'over-the-counter',
    'silverneakers',
    'silversneeker',
    'silver sneeker',
    'transportion',
    'transportaton',
    'vitimins',
    'vitamen',
    'vitamens',
    'home nurse',
    'home nursing aide',
    'grocery',
    'food benefit',
    'life-alert',
    'meal program',
    'workout program',
    'exercise class',
    'copay',
    'co-pay',
    'coinsurance',
    'copayment',
    'co-payment',
    'co-insurance',
    'coinsurrance'
  ]
}

const SECTION_META: Readonly<Record<string, Omit<PlanSearchResult, 'key'>>> = {
  costs: { sectionId: BenefitSection.CostEssentials },
  doctors: { sectionId: BenefitSection.RegularAndPreventive },
  drugs: { sectionId: BenefitSection.DrugsAndPharmacy },
  pharmacy: { sectionId: BenefitSection.DrugsAndPharmacy },
  dental: { sectionId: BenefitSection.Dental },
  hearing: { sectionId: BenefitSection.Hearing },
  vision: { sectionId: BenefitSection.Vision },
  preventive: { sectionId: BenefitSection.RegularAndPreventive },
  emergency: { sectionId: BenefitSection.Emergency },
  mental: { sectionId: BenefitSection.MentalHealth },
  diagnostic: { sectionId: BenefitSection.Diagnostic },
  equipment: { sectionId: BenefitSection.EquipmentAndDiabetic },
  therapy: { sectionId: BenefitSection.Therapy },
  moreBenefits: { sectionId: BenefitSection.MoreBenefits }
}

const STOP_WORDS = new Set(['and', 'or', 'the', 'for', 'of', 'my', 'a', 'an', 'in', 'to', 'with'])

function tokenize(query: string): string[] {
  return query
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter((t) => t.length >= 2 && !STOP_WORDS.has(t))
}

export class PlanDetailsSearchMap {
  private static _matchSynonyms(regex: RegExp): PlanSearchResult[] {
    const seen = new Set<string>()
    const results: PlanSearchResult[] = []
    for (const [key, terms] of Object.entries(SEARCH_SYNONYMS)) {
      if (!seen.has(key) && terms.some((term) => regex.test(term))) {
        if (key in SECTION_META) {
          seen.add(key)
          results.push({ key, ...SECTION_META[key] })
        }
      }
    }
    return results
  }

  static search(query: string): PlanSearchResult[] {
    if (query.trim().length < 2) return []

    const tokens = tokenize(query)
    if (tokens.length === 0) return []

    // Try the full query as a phrase prefix first — this keeps multi-word
    // queries precise when short words (e.g. "b" in "part b") are filtered
    // out by the tokenizer and would otherwise broaden the results.
    const phrase = query
      .trim()
      .toLowerCase()
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const phraseResults = this._matchSynonyms(new RegExp(`^${phrase}`, 'i'))
    if (phraseResults.length > 0) return phraseResults

    // Fall back to token-by-token matching
    const seen = new Set<string>()
    const results: PlanSearchResult[] = []

    for (const token of tokens) {
      const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const wordStart =
        token.length >= 6
          ? new RegExp(`(?:^|[\\s-])${escaped}`, 'i')
          : token.length === 2
          ? new RegExp(`^${escaped}$`, 'i')
          : new RegExp(`^${escaped}`, 'i')

      for (const result of this._matchSynonyms(wordStart)) {
        if (!seen.has(result.key)) {
          seen.add(result.key)
          results.push(result)
        }
      }
    }

    return results
  }
}
